import type { ApCreditDebitNoteListRow } from '@dcc-erp/shared';
import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';

import { useApCreditDebitNotes } from '../../lib/queries/ap';

// =============================================================================
// Credit/Debit-notes tab (AP Phase C2) — lists AP credit/debit notes from
// useApCreditDebitNotes. Each note is a DRAFT/POSTED/VOID header whose engine
// 4-eyes chain is driven by ApNoteApprovalPanel; clicking a row opens that panel
// via the onOpenNote callback (wired to setNotePanel in ApPage), making the note
// lifecycle reachable in-app. Mirrors ApPaymentRunsTab structure + tbl-shell.
//
// WS9 — extracted verbatim from the ap.tsx route monolith. Presentational: the
// /ap route owns the row-click wiring via onOpenNote; this stays render-only.
// Guarded by ap.notes-tab.test.tsx (mounts <ApPage/>, clicks ใบลด/เพิ่มหนี้).
// =============================================================================
export interface ApNotesTabProps {
  companyId: string | undefined;
  onOpenNote: (note: ApCreditDebitNoteListRow) => void;
}

// noteType badge — ใบลดหนี้ (CREDIT) / ใบเพิ่มหนี้ (DEBIT).
function noteTypeBadge(noteType: ApCreditDebitNoteListRow['noteType']) {
  return noteType === 'CREDIT' ? (
    <span className="badge positive">
      <span className="bdot" />
      ใบลดหนี้
    </span>
  ) : (
    <span className="badge warning">
      <span className="bdot" />
      ใบเพิ่มหนี้
    </span>
  );
}

// Accounting-status badge for a note — the engine walk lives in approvalStatus,
// but the row shows the accounting terminal (POSTED/VOID) + PENDING in between.
function noteStatusBadge(note: ApCreditDebitNoteListRow) {
  const eng = note.approvalStatus;
  if (note.status === 'POSTED')
    return (
      <span className="badge positive">
        <span className="bdot" />
        ลงบัญชีแล้ว
      </span>
    );
  if (note.status === 'VOID')
    return (
      <span className="badge negative">
        <span className="bdot" />
        ยกเลิก
      </span>
    );
  // DRAFT accounting status: distinguish PENDING (in the engine) from a not-yet-
  // submitted draft for clarity.
  if (eng === 'PENDING')
    return (
      <span className="badge warning">
        <span className="bdot" />
        รออนุมัติ
      </span>
    );
  if (eng === 'RETURNED')
    return (
      <span className="badge warning">
        <span className="bdot" />
        ตีกลับ
      </span>
    );
  return (
    <span className="badge info">
      <span className="bdot" />
      ร่าง
    </span>
  );
}

export function ApNotesTab({ companyId, onOpenNote }: ApNotesTabProps) {
  const { data: notes = [] } = useApCreditDebitNotes(companyId);

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ justifyContent: 'space-between' }}>
        <div className="muted" style={{ fontSize: 13 }}>
          ใบลดหนี้ / ใบเพิ่มหนี้ — คลิกแถวเพื่อเปิดสายอนุมัติ
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 480 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>ประเภท</th>
              <th>บิลอ้างอิง</th>
              <th>ผู้ขาย</th>
              <th>วันที่</th>
              <th className="num">ยอดรวม</th>
              <th>สถานะ</th>
            </tr>
          </thead>
          <tbody>
            {notes.length === 0 && (
              <tr>
                <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 20 }}>
                  ไม่พบใบลดหนี้ / ใบเพิ่มหนี้
                </td>
              </tr>
            )}
            {notes.map((note) => (
              <tr
                key={note.id}
                style={{ cursor: 'pointer' }}
                data-testid={`note-row-${note.id}`}
                onClick={() => onOpenNote(note)}
              >
                <td className="code">{note.voucherNo}</td>
                <td>{noteTypeBadge(note.noteType)}</td>
                <td className="code">{note.billVoucherNo ?? '—'}</td>
                <td>{note.vendorName ?? '—'}</td>
                <td>{fmtDate(note.noteDate)}</td>
                <td className="num tnum">
                  <b>{fmt(toNumber(note.totalAmount), 2)}</b>
                </td>
                <td>{noteStatusBadge(note)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
